/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import type { ReactNode } from 'react'
import colors from '@sportninja/common/constants/appColors'
import type { OperationsNode } from '@sportninja/common/features/competition/operations/types/operations.types'
import { paragraphXSmall, weightMedium } from 'src/components/css'

interface PanelBreadcrumbProps {
  ancestors: OperationsNode[]
  /** The selected node — the trailing crumb, not clickable. */
  current: OperationsNode
  /** Selects a crumb's node in the tree. */
  onSelectNode: (nodeId: string) => void
}

interface Crumb {
  key: string
  name: string
}

const Separator = () => (
  <span css={sepStyle} aria-hidden>
    ›
  </span>
)

/**
 * Selects the crumb's node in the tree rather than navigating away — the rail
 * holds selection and expansion state the user would otherwise lose, and the
 * panel already renders whatever the tree has selected.
 */
const CrumbButton = ({
  onClick,
  children,
}: {
  onClick: () => void
  children: ReactNode
}) => (
  <button type='button' onClick={onClick} css={linkStyle}>
    {children}
  </button>
)

const PanelBreadcrumb = ({
  ancestors,
  current,
  onSelectNode,
}: PanelBreadcrumbProps) => {
  'use memo'

  // The organization and the root season are both already in the screen header
  // above this panel, so repeating them here is noise. `ancestors` runs
  // root-first, which makes the season the entry to drop.
  const crumbs: Crumb[] = ancestors
    .filter((ancestor) => ancestor.type !== 'season')
    .map((ancestor) => ({ key: ancestor.id, name: ancestor.name }))

  // With the org and season gone, a season or conference selection has nothing
  // left to show but its own name — which is the panel title directly below.
  if (crumbs.length === 0) {
    return null
  }

  return (
    <nav css={crumbStyle} aria-label='Breadcrumb'>
      {crumbs.map((crumb) => (
        <span key={crumb.key}>
          <CrumbButton onClick={() => onSelectNode(crumb.key)}>
            {crumb.name}
          </CrumbButton>
          <Separator />
        </span>
      ))}
      <span css={currentStyle}>{current.name}</span>
    </nav>
  )
}

/* No bottom margin: the parent row owns the spacing below. */
const crumbStyle = css`
  ${paragraphXSmall}
  ${weightMedium}
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  color: ${colors.NEUTRAL_100};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const linkStyle = css`
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  color: inherit;
  /* Buttons don't inherit text-transform from the crumb row (the UA resets it
     on form controls), so without this the clickable ancestor crumbs render in
     their source casing while only the trailing span-crumb is uppercased. */
  text-transform: inherit;
  cursor: pointer;

  &:hover {
    color: ${colors.PRIMARY};
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: 2px;
  }
`

/* SECONDARY_200 was all but invisible against the panel — the chevron needs to
   read as a separator at a glance, so it is brighter and a step larger than
   the crumb text it sits between. */
const sepStyle = css`
  color: ${colors.NEUTRAL_100};
  margin-left: 6px;
  font-size: 15px;
  line-height: 1;
  vertical-align: middle;
  /* Not a letter — the crumbs' tracking would push it off its neighbours. */
  letter-spacing: 0;
`

const currentStyle = css`
  color: ${colors.NEUTRAL_50};
`

export default PanelBreadcrumb
